import { useRef, useState, KeyboardEvent, ClipboardEvent } from 'react';
import { cn } from '../../../lib/utils';

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  onComplete,
  error,
  disabled = false,
}: OTPInputProps) {
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Initialize refs array
  if (inputRefs.current.length !== length) {
    inputRefs.current = Array(length)
      .fill(null)
      .map((_, i) => inputRefs.current[i] || null);
  }

  const digits = value.padEnd(length, ' ').split('').slice(0, length);

  const focusInput = (index: number) => {
    const targetIndex = Math.max(0, Math.min(length - 1, index));
    inputRefs.current[targetIndex]?.focus();
    setFocusedIndex(targetIndex);
  };

  const handleChange = (index: number, digit: string) => {
    if (disabled) return;

    // Only allow digits
    if (digit && !/^\d$/.test(digit)) return;

    const newDigits = [...digits];
    newDigits[index] = digit;
    const newValue = newDigits.join('').trim();

    onChange(newValue);

    // Auto-focus next input
    if (digit && index < length - 1) {
      focusInput(index + 1);
    }

    // Call onComplete when all digits are filled
    if (newValue.length === length) {
      onComplete?.(newValue);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    // Backspace: clear current or move to previous
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[index] !== ' ') {
        handleChange(index, '');
      } else if (index > 0) {
        focusInput(index - 1);
        handleChange(index - 1, '');
      }
    }
    // Arrow left
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }
    // Arrow right
    else if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
    // Home
    else if (e.key === 'Home') {
      e.preventDefault();
      focusInput(0);
    }
    // End
    else if (e.key === 'End') {
      e.preventDefault();
      focusInput(length - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    const pastedData = e.clipboardData.getData('text/plain');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, length);

    if (pastedDigits) {
      onChange(pastedDigits);
      focusInput(Math.min(pastedDigits.length, length - 1));

      if (pastedDigits.length === length) {
        onComplete?.(pastedDigits);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 justify-center">
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digits[index] === ' ' ? '' : digits[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => setFocusedIndex(index)}
            onPaste={handlePaste}
            disabled={disabled}
            autoFocus={index === 0}
            className={cn(
              'w-12 h-14 text-center text-2xl font-light rounded-xl border transition-all',
              'focus:outline-none focus:ring-2 focus:ring-slate-900/10',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error
                ? 'border-red-500 focus:border-red-500'
                : focusedIndex === index
                ? 'border-slate-900'
                : 'border-slate-200',
              digits[index] !== ' ' && !error && 'bg-slate-50'
            )}
          />
        ))}
      </div>

      {error && (
        <p className="text-sm text-red-600 font-medium text-center">{error}</p>
      )}
    </div>
  );
}
